/**
	* Map layer tool view
	* Handles: map layer switching
	* @module MapLayerTool
*/

/// Imports

// React
import React, { useState, useEffect } from 'react';

// jQuery
import $ from 'jquery';

// OpenLayers
import OlMap from 'ol/Map';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import LayerGroup from 'ol/layer/Group';
import TileGrid from 'ol/tilegrid/TileGrid';
import { getWidth } from 'ol/extent';
import {transformExtent} from 'ol/proj';

// Styles
import 'styles/css/DrawTool.scss';
import 'styles/css/MapLayerTool.scss';

// Models
import MapToolMode from 'lib/enums/maptoolmode';
import OpusLayerGroup from 'lib/classes/opuslayergroup';
import OpusLayer from 'lib/classes/opuslayer';
import MapLayerToolView from 'lib/enums/maplayertoolview';
import { getToolEnabled } from 'lib/utilities/toolmodes';
import { getViewModeClassname, getViewModeContainerName } from 'lib/utilities/maplayertoolview';
import { Popup } from 'lib/classes/popup';
import { 
	limitString, 
	removeFromArray, 
	addToArrayUnique, 
	unshiftToArrayUnique,
	matchFilter
} from 'lib/utilities/misc';

// Lang
import { getLocaleString } from 'lib/localization/vendor';

// UI
import HelpMapLayersPopup from 'views/popups/HelpMapLayers';

// Tools
import { updateURLParameter } from 'lib/utilities/misc';
import { 
	getAllOpusLayersVisible, 
	getOpusLayersVisible, 
	getOpusLayersCollapsed, 
	getAllOpusLayersCollapsed, 
	getVisibleLayerCodes,
	getCollapsedLayerGroupIds
} from 'lib/utilities/map';
import { assetURL } from 'lib/utilities/asset';

/// Variable declarations
var mapConfig:any;
var layerGroups:OpusLayerGroup[];
var initialized:boolean = false;

const mapLayers:Map<string, TileLayer<TileWMS>> = new Map();

import 'styles/css/MapLayerTool.scss';

interface MapLayerToolProps {
	olMap:OlMap,
	currentToolMode:MapToolMode,
}

export default function MapLayerTool({olMap, currentToolMode}:MapLayerToolProps) {
	mapConfig = olMap.get('opus-config');
	layerGroups = olMap.get('opus-layer-groups');
	
	const extent = olMap.getView().getProjection().getExtent();
	const startResolution = getWidth(extent) / 256;
	const resolutions:number[] = [];
	
	for(let i = 0; i < 22; i++)
		resolutions[i] = startResolution / Math.pow(2, i);
	
	const tileGrid = new TileGrid({
		extent,
		resolutions,
		tileSize: 512
	});
	
	// State hooks
	const [overviewMapMinimized, setOverviewMapMinimized] = useState<boolean>(false);
	const [viewMode, setViewMode] = useState<MapLayerToolView>(MapLayerToolView.Rows);
	const [groupsCollapsed, setGroupsCollapsed] = useState<number[]>([]);
	const [layersVisible, setLayersVisible] = useState<string[]>([]);
	const [_layerGroups, _setLayerGroups] = useState<OpusLayerGroup[]>([]); // Dummy state value, not used
	const [filter, setFilter] = useState<string>('');
	const [toggleAllLayersSwitch, setToggleAllLayersSwitch] = useState<boolean>(getAllOpusLayersVisible(olMap));
		
	useEffect(() => {
		scaleRect();
	}, [_dimensions, currentToolMode, overviewMapMinimized, olMap.get('opus-config'), _config.lang.locale]);
	
	// Reload layers when map config altered
	useEffect(() => {
		loadLayers();
	}, [mapConfig]);
	
	// Initialize
	const initialize = () => {
		loadLayers();
		setupEvents();
			
		initialized = true;
	}
	
	// Setup events
	const setupEvents = () => {
		// Allow the toggle method to be called from anywhere in the project via the events system
		// For example, it is invoked in PinDropTool to enable a search result layer when clicked
		_events.on('toggle-layer', (opusLayer:OpusLayer, visible:boolean) => {
			toggleLayer(opusLayer, visible);
		});
		
		// Toggle layer by code only (e.g. in RasterImageTool)
		_events.on('toggle-layer-code', (code:string, visible:boolean) => {
			let opusLayer:OpusLayer;
			
			layerGroups.forEach((layerGroup:OpusLayerGroup) => {
				layerGroup.layers.forEach((layer:OpusLayer) => {
					if(layer.id == code)
						opusLayer = layer;
				})
			});
			
			opusLayer && toggleLayer(opusLayer, visible);
		});
		
		// From opuslayer class setVisible to the actual state being updated
		_events.on('layer-toggled', () => onLayersUpdated());
		
		// UI considerations
		_events.on('overview-map-minimized', (minimized:boolean) => {
			setTimeout(() => setOverviewMapMinimized(minimized), 100);
		});
		
		// Add and remove layer groups
		_events.on('add-layer-group', (lg:OpusLayerGroup) => {
			if(unshiftToArrayUnique(lg, layerGroups)) {
				lg.layers.forEach((opusLayer:OpusLayer) => createMapLayer(opusLayer));
				
				_events.invoke('layer-update');
				refreshHack();
			}
		});
		_events.on('remove-layer-group', (lg:OpusLayerGroup) => {
			if(removeFromArray(lg, layerGroups)) {
				lg.layers.forEach((opusLayer:OpusLayer) => removeMapLayer(opusLayer));
				
				_events.invoke('layer-update');
				refreshHack();
			}
		});
		
		// Set layer group hidden
		_events.on('hide-layer-group', (lg:OpusLayerGroup, hidden:boolean) => {
			lg.isHidden = hidden;
			refreshHack();
		});
		_events.on('hide-layer', (opusLayer:OpusLayer, hidden:boolean) => {
			opusLayer.isHidden = hidden;
			refreshHack();
		});
	}
	
	/*
		* Refresh hack
		* @remarks
		* This is only necessary because rather than using react state to store opus layer groups
		* (we are using the property opus-layer-groups on olMap instead)
		* This is because we need to access these groups easily outside of this module
		* 
		* The state object _layerGroups stores the layer groups too, but is not used.
	*/
	const refreshHack = () => {
		_setLayerGroups([...layerGroups]);
	}
	
	/*
		Called after this tool's state object has had its layers updated.
	*/
	const onLayersUpdated = () => {
		syncMapLayers();
		setLayersVisible(getVisibleLayerCodes(olMap));
		setGroupsCollapsed(getCollapsedLayerGroupIds(olMap));
	}
	
	/**
		Load layers
	*/
	const loadLayers = () => {
		const syncLayerCodes:string[] = olMap.get('sync-layer-codes');
		
		removeMapLayers();
		layerGroups.splice(0, layerGroups.length);
		
		mapConfig.legend.layerGroups.forEach((lg:any) => {
			const opusLayerGroup:OpusLayerGroup = new OpusLayerGroup({
				id: lg.id,
				name: lg.name,
				colour: lg.colour,
				isHidden: lg.isHidden,
				layers: [],
				legendOrder: lg.legendOrder,
				collapsed: true,
				json: lg.json
			});
			
			lg.layers.forEach((l:any) => {
				let defaultVisible:boolean = true;
				
				if(_config.defaultLayers)
					defaultVisible = _config.defaultLayers?.indexOf(l.id) > -1;
					
				if(syncLayerCodes)
					defaultVisible = syncLayerCodes.includes(l.id);
				
				const opusLayer:OpusLayer = new OpusLayer({
					id: l.id,
					name: l.name,
					legendOrder: l.legendOrder,
					cartographicOrder: l.cartographicOrder,
					isHidden: l.isHidden,
					images: l.images,
					group: opusLayerGroup,
					visible: defaultVisible && _config.showAllLayersOnStart && !l.notVisibleOnStart,
					json: l.json
				});
				
				opusLayerGroup.layers.push(opusLayer);
				createMapLayer(opusLayer);
			})
			
			layerGroups.push(opusLayerGroup);
		});
		
		refreshHack();
	}
	
	/**
		Manage individual WMS tile layers
	*/
	const createMapLayer = (opusLayer:OpusLayer) => {
		if(opusLayer.id == _config.rastersLegendId || opusLayer.id == _config.annotationLegendId)
			return;
		
		if(mapLayers.has(opusLayer.id))
			return;
			
		const bounds:any = mapConfig.panZoom.maxExtents;
		const extent:number[] = transformExtent(
			[
				bounds.left,
				bounds.bottom,
				bounds.right,
				bounds.top
			],
			mapConfig.epsgCode,
			mapConfig.epsgCodeView
		);
		const urls:string[] = mapConfig.legend.wmsFeeds.map((url:string) => url.startsWith('//') ? `https:${url}` : url);
		const layer:TileLayer<TileWMS> = new TileLayer<TileWMS>({
			source: new TileWMS({
				urls,
				params: {
					LAYERS: opusLayer.id,
					VERSION: '1.1.1',
					SRS: mapConfig.epsgCode
				},
				crossOrigin: _config.crossOrigin,
				projection: mapConfig.epsgCode,
				tileGrid,
				hidpi: false
			}),
			extent,
			visible: opusLayer.getVisible(),
			zIndex: opusLayer.cartographicOrder + 2
		});
		
		mapLayers.set(opusLayer.id, layer);
		
		if(_config.showLayers)
			olMap.addLayer(layer);
	}
	const removeMapLayer = (opusLayer:OpusLayer) => {
		const layer:TileLayer<TileWMS> = mapLayers.get(opusLayer.id);
		if(!layer)
			return;
		
		olMap.removeLayer(layer);
		mapLayers.delete(opusLayer.id);
	}
	const removeMapLayers = () => {
		mapLayers.forEach((layer:TileLayer<TileWMS>) => {
			olMap.removeLayer(layer);
		});
		mapLayers.clear();
	}
	const syncMapLayers = () => {
		layerGroups.forEach((layerGroup:OpusLayerGroup) => {
			layerGroup.layers.forEach((opusLayer:OpusLayer) => {
				mapLayers.get(opusLayer.id)?.setVisible(opusLayer.getVisible());
			});
		});
	}
	
	const drawFilter = () => {
		return (
			<input 
				type='search' 
				placeholder={getLocaleString('search')}
				onChange={(e) => setFilter(e.target.value)} 
				value={filter}
				autoComplete='off'
				tabIndex={300}
			/>
		)
	}
	
	/**
		Draw layer groups
	*/
	const drawLayerGroups = () => {
		let rows:any = [];
		
		const layerGroupsSorted:OpusLayerGroup[] = [...layerGroups].sort(
			(a:OpusLayerGroup, b:OpusLayerGroup) => {
				return a.legendOrder - b.legendOrder;
			}
		);
		
		layerGroupsSorted.forEach((lg:OpusLayerGroup) => {
			if(!lg.shouldShowInLegend) 
				return;
			
			const layersSorted:OpusLayer[] = [...lg.layers].sort(
				(a:OpusLayer, b:OpusLayer) => {
					return a.legendOrder - b.legendOrder;
				}
			);
			
			const layersDraw:any[] = drawLayers(layersSorted);
			
			if(layersDraw.length < 1)
				return;
			
			lg.updateAllVisible();
			
			const layerGroupId:string = `lg_${lg.id}`;
			const checked:boolean = lg.getVisible();
			const allChecked:boolean = lg.getAllVisible();
			const eyeImage:string = `assets/images/eye${!allChecked ? 'hide' : ''}.png`;
			const collapseImage:string = `assets/images/slide${lg.collapsed ? 'up' : 'down'}.png`;
			const toggleCollapse = () => toggleCollapseLayerGroup(lg, !lg.collapsed);
			const toggleVisible = () => toggleLayerGroup(lg, !(checked || allChecked));
			
			rows.push(
				<div className='layer-group' key={lg.id}>
					<div className='layer-group__title'>
						<div 
							className='layer-group__flex layer-group__layer-title' 
							onClick={toggleCollapse}
							onKeyDown={(e:any) => {
								if (e.key === 'Enter')
									toggleCollapse();
							}}
							tabIndex={rows.length + 301}
						>
							<div className='layer-group__flex layer-group__name'>
								<div className='circle' style={{
										background: lg.colour, 
										marginRight: 8
								}}></div>
							</div>
							<div className='layer-group__flex layer-group__name'>
								{lg.name}
							</div>
						</div>
						<div className='layer-group__flex layer-group__toggle'>
							<div 
								className={
									`toggle-btn ${lg.collapsed ? ` toggle-btn--selected ${_ui.getSelClassName()}` : ''}`
								} 
								onClick={() => toggleCollapse()} 
								data-tip={getLocaleString(lg.collapsed ? 'uncollapse' : 'collapse')} 
								data-delay-show='400'
							>
								<img src={assetURL(collapseImage)} /> 
							</div>
							<div 
								className={
									`toggle-btn ${allChecked ? ` toggle-btn--selected ${_ui.getSelClassName()}` : ''}`
								} 
								onClick={toggleVisible} 
								onKeyDown={(e:any) => {
									if (e.key === 'Enter')
										toggleVisible();
								}}
								data-tip={getLocaleString('togglegroup')} 
								data-delay-show='400'
								tabIndex={rows.length + 303}
							>
								<img src={assetURL(eyeImage)} /> 
							</div>
						</div>
					</div>
					<div id={layerGroupId}>
						<div className='layer-group__spacer'></div>
						<div className={`layers-container${getViewModeContainerName(viewMode)}`}>
							{layersDraw}
						</div>
					</div>
				</div>
			);
		});
		
		if(rows.length < 1)
			return (<small>{getLocaleString('nolayers')}</small>);
		
		return (
			<div className={`layer-groups${getViewModeClassname(viewMode)}`}>
				{rows}
			</div>
		);
	}
	
	/**
		* Draw layers
		* 
		* @param lg	-	Layer group to draw
	*/
	const drawLayers = (layers:OpusLayer[]) => {
		let rows:any = [];
		
		layers.forEach((opusLayer:OpusLayer) => {
			// This value ensures we know what's actually passing the below conditions
			opusLayer._visibleInLegendOMV = false;
			
			if(!opusLayer.shouldShowInLegend) 
				return;
			
			if(filter &&
				!matchFilter(opusLayer.name, filter)
			)
				return;
			
			if(opusLayer.id == _config.rastersLegendId && !getToolEnabled(MapToolMode.Upload, mapConfig))
				return;
			
			if(opusLayer.id == _config.annotationLegendId && !getToolEnabled(MapToolMode.Annotation, mapConfig))
				return;
			
			const checked:boolean = opusLayer.getVisible();
			const className:string = `layer-group__layer${checked ? ` layer-group__layer--selected ${_ui.getSelClassName()}` : ''}`;
			const eyeImage:string = `assets/images/eye${!checked ? 'hide' : ''}.png`;
			const size:number = 40;
			
			let url:string = opusLayer.images[0];
			
			url = updateURLParameter(url, 'width', size.toString());
			url = updateURLParameter(url, 'height', size.toString());
			
			let name:string = opusLayer.name;
			
			if(viewMode == MapLayerToolView.Grid)
				name = limitString(name, 40);
			else
			if(viewMode == MapLayerToolView.GridSmall)
				name = limitString(name, 30);
				
			opusLayer._visibleInLegendOMV = true;
			
			const onClick = () => toggleLayer(opusLayer, !checked);
			
			rows.push(
				<div 
					className={className} 
					data-tip={limitString(opusLayer.name, 50)} 
					data-delay-show='800' 
					onClick={onClick} 
					onKeyDown={(e:any) => {
						if (e.key === 'Enter')
							onClick();
					}}
					key={layers.indexOf(opusLayer)}
					tabIndex={layers.indexOf(opusLayer) + 302}
				>
					<div className='layer-row__meta'>
						<div className='layer-row__image'>
							<img src={url} /> 
						</div>
						<div className='layer-row__title'>
							{name}
						</div>
						<div className='layer-row__controls'>
							<div className='toggle-btn layer-row__control'>
								<img src={assetURL(eyeImage)} /> 
							</div>
						</div>
					</div>
				</div>
			);
		});
		
		return rows;
	}
	
	/**
		Draw close map layer tool button
	*/
	const drawCloseButton = () => {
		const onClick = () => _ui.setToolMode(MapToolMode.Select);
		
		return (
			<div 
				className={`toggle-btn toggle-btn--selected ${_ui.getSelClassName()}`}
				onClick={onClick}
				onKeyDown={(e:any) => {
					if (e.key === 'Enter')
						onClick();
				}}
				tabIndex={297}
			>
				<img src={assetURL('assets/images/cross.png')} />
			</div>
		)
	}
	
	/**
		Draw toggle button for all layer groups
	*/
	const drawAllToggleButton = () => {
		const allChecked:boolean = toggleAllLayersSwitch;
		const eyeImage:string = `assets/images/eye${!allChecked ? 'hide' : ''}.png`;
		
		const onClick = () => {
			toggleAllLayers(!allChecked);
			setToggleAllLayersSwitch(!toggleAllLayersSwitch);
		}
		
		return (
			<div 
				className={
					`toggle-btn ${allChecked ? ` toggle-btn--selected ${_ui.getSelClassName()}` : ''}`
				} 
				onClick={onClick} 
				onKeyDown={(e:any) => {
					if (e.key === 'Enter')
						onClick();
				}}
				data-tip={getLocaleString('toggleall')} 
				data-delay-show='700'
				tabIndex={296}
			>
				<img src={assetURL(eyeImage)} />
			</div>
		)
	}
	
	/**
		Draw collapse button for all layer groups
	*/
	const drawAllCollapseButton = () => {
		const allCollapsed:boolean = getAllOpusLayersCollapsed(olMap);
		//const anyCollapsed:boolean = groupsCollapsed.length > 0;
		const collapseImage:string = `assets/images/toggle${allCollapsed ? 'up' : 'down'}.png`;
		
		const onClick = () => toggleCollapseAllLayerGroups(!allCollapsed);
		
		return (
			<div 
				className={
					`toggle-btn ${allCollapsed ? ` toggle-btn--selected ${_ui.getSelClassName()}` : ''}`
				} 
				onClick={onClick} 
				onKeyDown={(e:any) => {
					if (e.key === 'Enter')
						onClick();
				}}
				data-tip={getLocaleString(allCollapsed ? 'uncollapseall' : 'collapseall')} 
				data-delay-show='400'
				tabIndex={295}
			>
				<img src={assetURL(collapseImage)} /> 
			</div>
		)
	}
	
	/**
		Draw view mode buttons
	*/
	const drawViewModeButtons = () => {
		const drawViewModeButton = (viewModeDraw:MapLayerToolView) => {
			const viewModeName:string = MapLayerToolView[viewModeDraw].toLowerCase();
			const checked:boolean = viewMode == viewModeDraw;
			const className:string = `toggle-btn ${checked ? ` toggle-btn--selected ${_ui.getSelClassName()}` : ''}`;
			
			const clickViewModeButton:Function = () => {
				setViewMode(viewModeDraw);
				_ui.tooltip.hide();
			}
			const onClick = () => clickViewModeButton();
			
			return (
				<div 
					className={className} 
					onClick={onClick} 
					onKeyDown={(e:any) => {
						if (e.key === 'Enter')
							onClick();
					}}
					data-tip={getLocaleString(viewModeName)} 
					data-delay-show='700'
					tabIndex={290 + Number(viewModeDraw)}
				>
					<img src={assetURL(`assets/images/${viewModeName}view.png`)} />
				</div>
			)
		}
		
		return (
			<>
				{drawViewModeButton(MapLayerToolView.Rows)}
				{drawViewModeButton(MapLayerToolView.Grid)}
				{drawViewModeButton(MapLayerToolView.GridSmall)}
			</>
		)
	}
	
	/**
		* Toggle layer
		* 
		* @param opusLayer	-	Opus layer to toggle
		* @param visible	-	visible/invisible
	*/
	const toggleLayer = (opusLayer:OpusLayer, visible:boolean) => {
		opusLayer.setVisible(visible);
		
		_ui.tooltip.hide();
	}
	
	/**
		* Toggle layer group
		* 
		* @param opusLayerGroup		-	Opus layer group to toggle
		* @param visible			-	visible/invisible
	*/
	const toggleLayerGroup = (opusLayerGroup:OpusLayerGroup, visible:boolean) => {
		opusLayerGroup.setVisible(visible);
		
		_ui.tooltip.hide();
	}
	
	/**
		* Toggle collapse layer group
		* 
		* @param opusLayerGroup		-	Opus layer group to toggle
		* @param visible			-	collapsed/uncollapsed
	*/
	const toggleCollapseLayerGroup = (opusLayerGroup:OpusLayerGroup, collapsed:boolean) => {
		opusLayerGroup.collapsed = collapsed;
		
		if(opusLayerGroup.collapsed) {
			$(`#lg_${opusLayerGroup.id}`).slideDown();
		}else{
			$(`#lg_${opusLayerGroup.id}`).slideUp();
		}
		
		setGroupsCollapsed(getCollapsedLayerGroupIds(olMap));
		
		_ui.tooltip.hide();
	}
	
	/**
		* Toggle all the layer groups collapsed/uncollapsed
		* 
		* @param {boolean} collapsed - Set it to collapsed/uncollapsed?
	*/
	const toggleCollapseAllLayerGroups = (collapsed:boolean) => {
		layerGroups.forEach((opusLayerGroup:OpusLayerGroup) => {
			toggleCollapseLayerGroup(opusLayerGroup, collapsed);
		});
		
		_ui.tooltip.hide();
	}
	
	/**
		* Toggle all the layers
		* 
		* @param {boolean} visible - Set it to visible/invisible?
	*/
	const toggleAllLayers = (visible:boolean) => {
		layerGroups.forEach((opusLayerGroup:OpusLayerGroup) => {
			toggleLayerGroup(opusLayerGroup, visible);
		});
		
		_ui.tooltip.hide();
	}
	
	/**
		Dynamically scale the map layer tool rect to correctly fill the screen height
	*/
	
	const scaleRect = () => {
		let height:number = _dimensions.height;
		
		const paddingBottom:number = height > 500 ? 100 : 80;
		
		height -= $('#attributions').height();
		
		if($('#overview-map')?.is(':visible'))
			height -= $('#overview-map').height();
		
		height -= paddingBottom;
		
		$('#layer-tool-rect').css('max-height', height);
		$('#layer-results-rect').css('max-height', height); // Do the pin drop tool search result rect here too
	}
	
	/*
		Get is select tool selected
	*/
	const isToolSelected = ():boolean => {
		return currentToolMode == MapToolMode.MapLayers;
	}
	
	// Register the display of the right panel
	_ui.registerRightPanel(isToolSelected(), 'layer-tool-rect');
	
	/*
		Transition up and down for the tool panel
	*/
	useEffect(() => {
		if(!isToolSelected()) {
			$('#layer-tool-rect').slideUp(_ui.transitionUpOptions);
		}else{
			$('#layer-tool-rect').delay(200).slideDown(_ui.transitionDownOptionsFlex);
		}
	}, [currentToolMode]);
	
	// Initialize if not
	if(!initialized)
		initialize();
	
	return (
		<>
			<div id='layer-tool-rect' className='rect rect__toolbox--right rect__toolbox--hidden rect__toolbox--expanding'>
				<div className='rect__toolbox__heading'>
					{getLocaleString('layers')}
					
					<div className='rect__toolbox__right'>
						{drawViewModeButtons()}
						{drawAllCollapseButton()}
						{drawAllToggleButton()}
						{drawCloseButton()}
					</div>
				</div>
				
				<div className='map-layer-tool__layers-filter'>
					{drawFilter()}
				</div>
				
				<div className='map-layer-tool__content rect__content'>
					{drawLayerGroups()}
				</div>
			</div>
		</>
	)
}